import React, { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useObservableImmutable } from '@core/Utils/hooks'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import {
  FallBackBanner,
  LinkWrapper,
  BannerContainer as Container,
  CarouselWrapper,
  BannerPicture,
} from './Components'
import type { BannerApiResource, BannerArea, Banner as BannerType } from './types'
import { trackBannerViewed } from './analytics'
import { filterBannersForArea } from './helpers/filterBannersForArea'
import { fetchBannersFromApi } from './helpers/api'

export const Banner = React.memo(({ areaName }: { areaName: BannerArea }) => {
  const { accountNumber } = useObservableImmutable(userAccountState$, ['accountNumber'])
  const { isLoading, error, data } = useQuery({
    queryKey: [queryKeys.banner],
    queryFn: fetchBannersFromApi,
    refetchInterval: 1000 * 60 * 2, // 2min
    refetchOnMount: false,
    refetchOnWindowFocus: true,
  })

  const viewedBannersRef = useRef<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)

  if (isLoading || accountNumber === undefined) {
    return <Container areaName={areaName} ref={containerRef} />
  }

  const hasData = data && data.length > 0
  const banners = hasData
    ? filterBannersForArea({ areaName, accountNumber, data }).map(mapApiBanner)
    : []

  const handleViewChange = (index: number) => {
    const bannerElement = containerRef.current
    const isInViewPort =
      bannerElement &&
      bannerElement.getBoundingClientRect().top > 0 &&
      bannerElement.getBoundingClientRect().top < window.innerHeight

    if (isInViewPort && !viewedBannersRef.current.has(banners[index].name) && banners[index].name) {
      const bannerData = {
        bannerName: banners[index].name || '',
        internalTrackingCode: banners[index].internalTrackingCode,
        accountNumber: accountNumber,
        bannerLocation: areaName,
        bannerPosition: index,
        clickUrl: (areaName === 'Desktop' ? banners[index].desktopUrl : banners[index].url) || '',
        endDate: banners[index].endDate,
      }

      trackBannerViewed(bannerData)
      viewedBannersRef.current.add(banners[index].name)
    }
  }

  if (error || banners.length === 0)
    return (
      <Container areaName={areaName} ref={containerRef}>
        <FallBackBanner areaName={areaName} />
      </Container>
    )

  return (
    <Container areaName={areaName} ref={containerRef}>
      <CarouselWrapper key={accountNumber} onViewChange={handleViewChange}>
        {banners.map((banner, index) => (
          <LinkWrapper
            key={`${banner.image}_${index}`}
            url={areaName === 'Desktop' ? banner.desktopUrl : banner.url}
            internalTrackingCode={banner.internalTrackingCode}
            abGroupingTokenPresent={banner.abGroupingTokenPresent}
            analyticsData={{
              bannerName: banner.name,
              internalTrackingCode: banner.internalTrackingCode,
              accountNumber: accountNumber,
              bannerLocation: areaName,
              bannerPosition: index,
              clickUrl: (areaName === 'Desktop' ? banner.desktopUrl : banner.url) || '',
              endDate: banner.endDate,
            }}
            isDesktop={areaName === 'Desktop'}
          >
            <BannerPicture image={banner.image} imageWide={banner.imageWide} areaName={areaName} />
          </LinkWrapper>
        ))}
      </CarouselWrapper>
    </Container>
  )
})

Banner.displayName = 'Banner'

// =============
// Local Helpers
// =============

function mapApiBanner(resource: BannerApiResource): BannerType {
  return {
    name: resource.Name,
    image: resource.Image.File.Url,
    imageWide: resource.ImageWide.File.Url,
    imageText: resource.Image.Title,
    url: resource.Link,
    desktopUrl: resource.DesktopLink,
    internalTrackingCode: resource.InternalTrackingCode || undefined,
    abGroupingTokenPresent: !!resource.AbGroupingToken,
    endDate: resource.EndDate,
  }
}
